import PropTypes from "prop-types";
import styles from "./Button.module.css";

/**
 * Standard button component following project design patterns.
 * Uses transform scale on hover instead of background color changes.
 *
 * @example
 * // Primary button (save, submit) - default size
 * <Button variant="primary" onClick={handleSave}>Save</Button>
 *
 * @example
 * // Secondary button (cancel, back)
 * <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
 *
 * @example
 * // Small button
 * <Button variant="primary" size="sm" onClick={handleClick}>Small</Button>
 *
 * @example
 * // Extra small button
 * <Button variant="secondary" size="xs" onClick={handleClick}>XS</Button>
 *
 * @example
 * // Custom styling
 * <Button variant="primary" className={styles.customButton}>Custom</Button>
 */
const Button = ({
  children,
  variant = "primary",
  size = "normal",
  type = "button",
  disabled = false,
  onClick,
  className = "",
  ...props
}) => {
  const variantClass = variant === "secondary" ? styles.secondary : styles.primary;
  const sizeClass = styles[size] || styles.normal;
  const combinedClassName = `${styles.button} ${variantClass} ${sizeClass} ${className}`.trim();

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={combinedClassName}
      {...props}
    >
      {children}
    </button>
  );
};

Button.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(["primary", "secondary"]),
  size: PropTypes.oneOf(["normal", "sm", "xs"]),
  type: PropTypes.oneOf(["button", "submit", "reset"]),
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

export default Button;
